/**
 * Which kind of local-database failure this is, and what should be done about it.
 *
 * `DatabaseRecoveryService.interceptDatabaseError` is handed every database error the app
 * catches. The decision it needs lives here, in one pure function, with the differences between
 * the kinds of failure carried as DATA in `kDatabaseErrorFamilies`. The service does nothing but
 * perform the side effects the answer names.
 *
 * It is a separate module because it imports no Angular — only the predicates, which are plain
 * modules — so the decision can be unit-tested. The service cannot: it is loadable only in a
 * browser. Same shape, and for the same reason, as `app/cold-start-recovery.ts` one layer up.
 *
 * 🛑 THE ORDER OF THE GUARDS IN `decideDatabaseErrorAction` IS LOAD-BEARING, and every wrong
 * arrangement compiles, passes lint and behaves correctly in the ordinary case. What breaks, and
 * why this decision was lifted out of the service in the first place (df-4d553cd0ab53), is in
 * `design-docs/boot-sequence.md`, "A call on a handle we closed ourselves is not a broken
 * IndexedDB". Read that before moving anything in that function.
 */

import { isIdbError, isClosedDatabaseHandleError } from './error-patterns';
import { isSqliteOpenTimeoutError } from './pouchdb-adapter-capacitor-sqlite/sqlite-open-timeout';
import { isLocalStoreNotAnsweringError, localStoreNotAnsweringDatabaseId } from './local-store-timeout';

/** How soon after a successful graceful repair a second failure of the same family means the
 *  repair is not working, so the page should be reloaded instead of repaired again.
 *
 *  This used to be written out separately in the local-store arm and in the IndexedDB arm, with
 *  nothing keeping the two numbers in step. */
export const kGracefulRecoveryCooldownMs = 10_000;

/** What the app does when a family of failure cannot be repaired in place. */
export enum DatabaseErrorRemedy {
  /** Recreating the connection is a real remedy for this family: a fresh open often answers
   *  where a wedged one never will. Try that first, and let the recurrence rule escalate to a
   *  reload if it fails again straight away. */
  RecreateTheConnection = 'RecreateTheConnection',
  /** Recreating the connection fixes nothing for this family, so go straight to the reload path.
   *  Native SQLite has no IndexedDB-style graceful recovery at all. */
  ReloadThePage = 'ReloadThePage',
}

/** What every family carries, whatever its remedy.
 *
 *  Everything in here is data. The sequence the guards run in is NOT — that lives once, in
 *  `decideDatabaseErrorAction`. */
interface DatabaseErrorFamilyCommon {
  /** For reading in a test failure and in a log line. Not reported anywhere. */
  readonly name: string;

  /** Does this error belong to this family? */
  readonly matches: (error: any) => boolean;

  /** What to say in the console when our own destruction suppresses a failure of this family —
   *  and, by being here at all, that our own destruction suppresses it.
   *
   *  Present for the two families that come out of live stores: a purge and the sign-out wipe
   *  both make operations fail and make operations stop answering, so a failure then is the
   *  expected consequence of an action we just took. ABSENT for a SQLite open that timed out,
   *  which happens while opening rather than while using a store, and which the original arm
   *  therefore never suppressed.
   *
   *  One field rather than a flag plus a message, so the two cannot disagree and so no family
   *  carries wording that can never be printed. */
  readonly suppressionLogMessage?: string;

  /** The silent-problem name to file when a recovery is started for this family, or `undefined`
   *  to file nothing.
   *
   *  🛑 The IndexedDB family files NOTHING, and that is deliberate rather than an oversight: a
   *  genuine IndexedDB fault is reported by the recovery it triggers, not from here. Giving this
   *  family a name would start sending a new Sentry event for the commonest database failure
   *  there is. */
  readonly silentProblemName?: string;

  /** An extra question, asked AFTER the already-recovering guard and BEFORE any recovery is
   *  begun, that takes the error out of this family's ordinary handling.
   *
   *  Only the IndexedDB family has one, and it is the closed-handle case: a call on a store
   *  handle the app closed itself. Where it is asked from is the load-bearing part — see the
   *  comment on `DatabaseErrorActionKind.ClosedHandleWeClosedOurselves`. */
  readonly weClosedThisHandleOurselves?: (error: any) => boolean;

  /** Which database this error says failed, when the family can tell (#6788).
   *
   *  Only the silent-store family has one: its error names the database it gave up on, and a
   *  brand-new store of the user's own with nothing to lose is switched to a fresh name rather
   *  than reopened under the same one. See `DatabaseErrorActionKind.SwitchToFreshLocalStore`. */
  readonly failedDatabaseId?: (error: any) => string | undefined;
}

/** A family a fresh connection can genuinely fix.
 *
 *  Only these reach the "it failed again within the cooldown" rule, so only these have to say
 *  what to report when they do. */
export interface RepairableDatabaseErrorFamily extends DatabaseErrorFamilyCommon {
  readonly remedy: DatabaseErrorRemedy.RecreateTheConnection;
  /** What to say in the console when this family fails again inside the cooldown. */
  readonly recurrenceLogMessage: string;
  /** The `reason` on the `database_graceful_recovery_failed` event for that same case. */
  readonly recurrenceTelemetryReason: string;
}

/** A family a fresh connection cannot fix, so the page is replaced instead.
 *
 *  It carries no recurrence wording, and that is the point of splitting the type: the decision
 *  returns before the recurrence rule for these, so a `reason` string here could never be
 *  emitted — and a telemetry reason somebody can grep for, that no event ever carries, sends
 *  the next reader looking for data that does not exist. */
export interface UnrepairableDatabaseErrorFamily extends DatabaseErrorFamilyCommon {
  readonly remedy: DatabaseErrorRemedy.ReloadThePage;
}

/** One kind of local-database failure, and everything that makes it different from the others. */
export type DatabaseErrorFamily = RepairableDatabaseErrorFamily | UnrepairableDatabaseErrorFamily;

/** What `interceptDatabaseError` should do with an error it was handed. */
export enum DatabaseErrorActionKind {
  /** Not a local-database failure at all. The caller gets `false` and carries on handling it. */
  NotADatabaseError = 'NotADatabaseError',
  /** We are destroying these databases on purpose, so this failure is expected. Tell waiting
   *  callers to give up, leave a console breadcrumb, and run no recovery. */
  SuppressedByOurOwnDestruction = 'SuppressedByOurOwnDestruction',
  /** A recovery is already running. It will answer its own waiting callers, so do nothing at
   *  all here — including nothing that would settle the outcome out from under it. */
  LeaveItToTheRecoveryInFlight = 'LeaveItToTheRecoveryInFlight',
  /** A call on a store handle the app closed itself, outside a purge and the sign-out wipe.
   *  Our own lifecycle, not a broken device, so NO recovery runs: tell waiting callers that
   *  nothing is coming, and report the call.
   *
   *  🛑 Where this is decided is load-bearing in both directions, and `design-docs/boot-sequence.md`
   *  says what breaks either way. It must come AFTER the already-recovering guard, because a
   *  graceful recovery closes the old handle itself and so produces this very error — deciding it
   *  first would stand nearly every real recovery's own waiting callers down. And it must come
   *  BEFORE a recovery is begun, so that no recovery runs and the case stays out of the
   *  recurrence rule: a stale handle call is not evidence that IndexedDB is broken. */
  ClosedHandleWeClosedOurselves = 'ClosedHandleWeClosedOurselves',
  /** Start a recovery and go straight to the reload path — nothing can be repaired in place for
   *  this family. */
  ReloadThePage = 'ReloadThePage',
  /** Start a recovery, but a graceful repair of this family succeeded less than
   *  `kGracefulRecoveryCooldownMs` ago and it has failed again, so repairing it a second time is
   *  not going to work. Report why, then reload. */
  ReloadBecauseItFailedAgainTooSoon = 'ReloadBecauseItFailedAgainTooSoon',
  /** Start a recovery and try to recreate the connection in place. */
  RecreateTheConnection = 'RecreateTheConnection',
  /** The user's own store went silent and holds nothing the server lacks: it never finished a
   *  download here and never confirmed a write (#6788). Switch it to a fresh name and reload,
   *  which is what the error page's "start this device over" button does, without asking.
   *  Reopening the SAME name did not help either of the two iPhone buyers this was found on. */
  SwitchToFreshLocalStore = 'SwitchToFreshLocalStore',
}

/** The decision, plus the family it was reached for so the caller can read the family's data off
 *  it. Only `NotADatabaseError` carries no family — no family matched.
 *
 *  The recurrence wording rides on the ACTION rather than being read back off the family,
 *  because only here is it known to exist: the decision reaches that answer only for a family
 *  whose remedy is recreating the connection, and only those carry it. */
export type DatabaseErrorAction =
  | { readonly kind: DatabaseErrorActionKind.NotADatabaseError }
  | {
      readonly kind: Exclude<
        DatabaseErrorActionKind,
        | DatabaseErrorActionKind.NotADatabaseError
        | DatabaseErrorActionKind.SuppressedByOurOwnDestruction
        | DatabaseErrorActionKind.ReloadBecauseItFailedAgainTooSoon
      >;
      readonly family: DatabaseErrorFamily;
    }
  | {
      readonly kind: DatabaseErrorActionKind.SuppressedByOurOwnDestruction;
      readonly family: DatabaseErrorFamily;
      readonly suppressionLogMessage: string;
    }
  | {
      readonly kind: DatabaseErrorActionKind.ReloadBecauseItFailedAgainTooSoon;
      readonly family: DatabaseErrorFamily;
      readonly recurrenceLogMessage: string;
      readonly recurrenceTelemetryReason: string;
    };

/** What the decision needs to know about the recovery machinery's current state. */
export interface DatabaseRecoveryStateForDecision {
  /** Are we destroying our own databases right now — a destroy-bracketed purge, or the sign-out
   *  wipe? `DatabaseRecoveryService.isIntentionalDestruction()`. */
  readonly intentionalDestructionIsUnderway: boolean;
  /** Is a recovery already in flight, including its post-success cooldown?
   *  `DatabaseRecoveryService.startingDatabaseRecovery`. */
  readonly aRecoveryIsAlreadyStarting: boolean;
  /** How long ago the last graceful repair succeeded. Larger than any cooldown when none ever
   *  has. */
  readonly msSinceLastGracefulRecovery: number;
  /** May the app abandon this database's local store without asking (#6788)? Asked only for a
   *  family that can name the failed database. In production this is
   *  `mayAbandonSilentLocalStore` fed with the wrapper's facts. */
  readonly localStoreSafeToAbandon: (databaseId: string | undefined) => boolean;
}

/**
 * The families, in the order they are asked.
 *
 * The order is preserved from the hand-written arms it replaces. It matters because the
 * predicates are not mutually exclusive by construction: they are asked most-specific first, and
 * the IndexedDB one is the broadest.
 */
export const kDatabaseErrorFamilies: readonly DatabaseErrorFamily[] = [
  {
    // L11 (#3986): a bounded CsSQLite.open timed out (iOS native). The distinguishable
    // SqliteOpenTimeout error surfaced through the adapter callback. SQLite has no IndexedDB-style
    // graceful recovery, so this routes to the reload path instead of a silent forever-hang, and
    // it is reported from here because the low-level adapter has no LoggingService.
    name: 'sqlite-open-timeout',
    matches: isSqliteOpenTimeoutError,
    // no `suppressionLogMessage`: this fails while OPENING, not while using a store we are
    // tearing down, so our own destruction was never a reason to suppress it
    silentProblemName: 'SqliteOpenTimeout',
    remedy: DatabaseErrorRemedy.ReloadThePage,
  },
  {
    // #4401: a local store operation was given up on because the store answered NOTHING — not a
    // value, not an error. This is a different fact from an IndexedDB error, and it used to be
    // invisible: PouchDB's IndexedDB adapter has no time limit and no `onblocked` handler, so a
    // browser that never services the open request leaves every later operation queued for ever
    // with nothing to catch. In production that stopped boots for 30 seconds to over 4 minutes.
    //
    // It is routed like the IndexedDB family and NOT like the SQLite one: recreating the
    // connection is a real remedy here, so try that first and let the recurrence rule escalate.
    // `isIdbError` cannot recognise this — it matches an error object, and a store that goes
    // silent produces none — which is why this family has to exist at all.
    name: 'local-store-not-answering',
    matches: isLocalStoreNotAnsweringError,
    suppressionLogMessage: 'Suppressed local-store timeout caused by our own database destruction:',
    silentProblemName: 'LocalStoreNotAnswering',
    remedy: DatabaseErrorRemedy.RecreateTheConnection,
    // a store that goes silent again right after a graceful recreation is not going to be fixed
    // by another one
    recurrenceLogMessage: 'Database recovery: the local store went silent again within 10s of graceful recovery — falling back to reload',
    recurrenceTelemetryReason: 'local_store_silent_within_cooldown',
    failedDatabaseId: localStoreNotAnsweringDatabaseId,
  },
  {
    // a real database error: PouchDB's synthetic `indexed_db_went_bad`, or a raw IndexedDB
    // transaction error. The broadest predicate of the three, which is why it is asked last.
    name: 'indexeddb-fault',
    matches: isIdbError,
    suppressionLogMessage: 'Suppressed IndexedDB error caused by our own database destruction:',
    // 🛑 no silent-problem name ON PURPOSE — see `silentProblemName` on the interface above
    remedy: DatabaseErrorRemedy.RecreateTheConnection,
    recurrenceLogMessage: 'Database recovery: IDB error recurred within 10s of graceful recovery — falling back to reload',
    recurrenceTelemetryReason: 'recurred_within_cooldown',
    // the one family with an extra question: `isClosedDatabaseHandleError` is deliberately
    // narrower than `isIdbError`, which also matches genuine device faults that must keep being
    // recovered from
    weClosedThisHandleOurselves: isClosedDatabaseHandleError,
  },
];

/**
 * Decide what should happen to a database error. Pure — it reads nothing and changes nothing.
 *
 * 🛑 THE ORDER OF THE GUARDS BELOW IS THE WHOLE POINT OF THIS FUNCTION. It is stated once, here,
 * instead of being typed out again in every arm. Each step carries the reason it sits where it
 * does; `design-docs/boot-sequence.md`, "A call on a handle we closed ourselves is not a broken
 * IndexedDB", is the long version for the closed-handle step, which is the one every wrong
 * arrangement still compiles and still behaves correctly for in the ordinary case.
 */
export function decideDatabaseErrorAction(
  error: any,
  state: DatabaseRecoveryStateForDecision,
): DatabaseErrorAction {
  // for each family, most specific predicate first
  for (const family of kDatabaseErrorFamilies) {
    if (!family.matches(error)) {
      continue;
    }

    // FIRST: our own destruction. We are deleting these databases on purpose, so a failure now is
    // the expected consequence of an action we just took, not a fault — and no recovery must run,
    // because it would race the destruction and recreate the very stores being deleted. A family
    // with no `suppressionLogMessage` is one our own destruction never explains.
    if (family.suppressionLogMessage !== undefined && state.intentionalDestructionIsUnderway) {
      return {
        kind: DatabaseErrorActionKind.SuppressedByOurOwnDestruction,
        family,
        suppressionLogMessage: family.suppressionLogMessage,
      };
    }

    // SECOND: a recovery is already running. It owns the outcome every waiting caller is parked
    // on, so anything decided below this line would answer them on its behalf.
    if (state.aRecoveryIsAlreadyStarting) {
      return { kind: DatabaseErrorActionKind.LeaveItToTheRecoveryInFlight, family };
    }

    // THIRD: a call on a handle we closed ourselves. AFTER the guard above, because a graceful
    // recovery closes the old handle itself and so produces this very error while it runs; BEFORE
    // a recovery is begun, because none should run and the case must stay out of the recurrence
    // rule below.
    if (family.weClosedThisHandleOurselves?.(error)) {
      return { kind: DatabaseErrorActionKind.ClosedHandleWeClosedOurselves, family };
    }

    // FOURTH (#6788): the user's own store went silent and holds nothing the server lacks. A
    // fresh NAME is the remedy that works there, and it costs nothing, so switch rather than
    // reopen the same name. AFTER the guards above, because our own destruction and a recovery
    // already running both own the outcome. BEFORE the recurrence rule, because a store with
    // nothing to lose is better switched than reloaded into the same silent name.
    if (family.failedDatabaseId && state.localStoreSafeToAbandon(family.failedDatabaseId(error))) {
      return { kind: DatabaseErrorActionKind.SwitchToFreshLocalStore, family };
    }

    // FIFTH: from here a recovery IS begun. If a fresh connection fixes nothing for this family,
    // the page has to be replaced.
    if (family.remedy === DatabaseErrorRemedy.ReloadThePage) {
      return { kind: DatabaseErrorActionKind.ReloadThePage, family };
    }

    // SIXTH: we just repaired this gracefully and it has broken again straight away, so another
    // repair is not going to hold. The family is known repairable by now — the step above
    // returned for every other kind — which is what makes its recurrence wording readable here.
    if (state.msSinceLastGracefulRecovery < kGracefulRecoveryCooldownMs) {
      return {
        kind: DatabaseErrorActionKind.ReloadBecauseItFailedAgainTooSoon,
        family,
        recurrenceLogMessage: family.recurrenceLogMessage,
        recurrenceTelemetryReason: family.recurrenceTelemetryReason,
      };
    }

    // otherwise: recreate the connection in place, and fall back to a reload if that fails
    return { kind: DatabaseErrorActionKind.RecreateTheConnection, family };
  }

  return { kind: DatabaseErrorActionKind.NotADatabaseError };
}
